import { Box, Button, IconButton, Modal, Stack, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Tooltip, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from "react-router-dom";
import { MainBorderRadius, TableBgcolor, TableHeader } from '../../../app/AppStyles';
import { SearchKeyFilter } from '../../../app/AppUtils';
import ConfirmationModal from '../../subcomponents/ConfirmationModal';
import ContentLoader from '../../subcomponents/ContentLoader';
import SearchField from '../../subcomponents/SearchField';
import TablePaginationActions from '../../subcomponents/TablePaginationActions';
import { setAlertMessage, setAlertSeverity, setAlertStatus } from '../../subcomponents/alertSlice';
import ProgramEditCard from './ProgramEditCard';
import { deleteProgram, getPrograms, resetProgramStatus, selectProgram, setFailedProgram } from './programManagementSlice';

import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';

export default function ProgramTabs() {
    const dispatch = useDispatch();

    const [openEdit, setOpenEdit] = useState(false);
    const [openNew, setOpenNew] = useState(false);
    const [filter, setFilter] = useState("");

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { programs, programsStatus, deleteProgramStatus } = useSelector((state) => state.management.program);
    const { siomSettings } = useSelector((state) => state.settings.siom);

    useEffect(() => {
        dispatch(getPrograms());
    }, [dispatch]);

    useEffect(() => {
        if (deleteProgramStatus === "success") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.DELETE_SUCCESS}`));
            dispatch(setAlertSeverity("success"));
            dispatch(resetProgramStatus());
            dispatch(getPrograms());
        } else if (deleteProgramStatus === "failed") {
            dispatch(setAlertStatus(true));
            dispatch(setAlertMessage(`${resourceFinal[appLanguage].messages.ERROR_DELETE_FAILED}`));
            dispatch(setAlertSeverity("error"));
            dispatch(resetProgramStatus());
        };
    }, [dispatch, resourceFinal, appLanguage, deleteProgramStatus]);

    const newClick = () => {
        dispatch(selectProgram(null));
        dispatch(setFailedProgram(false));
        setOpenNew(true);
    };

    const callbackModalNew = () => {
        setOpenNew(false);
        dispatch(resetProgramStatus());
        dispatch(selectProgram(null));
        dispatch(setFailedProgram(false));
    };

    const callbackModalEdit = () => {
        setOpenEdit(false);
        dispatch(resetProgramStatus());
        dispatch(selectProgram(null));
        dispatch(setFailedProgram(false));
    };

    const TableFragment = () => {
        const navigate = useNavigate();
        const [page, setPage] = useState(0);
        const [rowsPerPage, setRowsPerPage] = useState(10);

        const handleFilterResult = (item) => {
            return SearchKeyFilter(item, ["name", "description"], filter);
        }

        const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - programs?.filter(handleFilterResult).length) : 0;

        const handleChangePage = (event, newPage) => {
            setPage(newPage);
        };

        const handleChangeRowsPerPage = (event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
        };

        const selectClick = (program) => {
            dispatch(selectProgram(program));
            navigate(`/programmabeheer/programma/${program.id}`);
        };

        const editClick = (program) => {
            dispatch(selectProgram(program));
            setOpenEdit(true);
        };

        const deleteClick = (programId) => {
            dispatch(deleteProgram(programId));
        };


        return (
            <>
                <TableBody>
                    {(rowsPerPage > 0 ? programs.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : programs.filter(handleFilterResult)).map(item =>
                        <TableRow key={item.id} sx={{ '&:nth-of-type(odd)': { bgcolor: 'white.main' }, height: 63 }}>
                            <TableCell sx={{ cursor: 'pointer' }} onClick={() => selectClick(item)} align="left">{item.name ? item.name : "-"}</TableCell>
                            <Tooltip title={item.description} placement="top-start">
                                <TableCell align="left" sx={{
                                    whiteSpace: "nowrap",
                                    maxWidth: "600px",
                                    minWidth: "100px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}>{item.description ? item.description : "-"}</TableCell>
                            </Tooltip>
                            <TableCell align="center" sx={{ display: 'none' }}>{item.greenFlagsNecessary ?? "-"}</TableCell>
                            {siomSettings?.enableAbsenceBlock && <TableCell align="center">{item.blockAfterAbsenceAmount ?? "-"}</TableCell>}
                            <TableCell align="center">{item.favoritesRequired ? resourceFinal[appLanguage].words.YES : resourceFinal[appLanguage].words.NO}</TableCell>
                            <TableCell align="center">{(item.favoritesMinimum > 0 && item.favoritesRequired) ? item.favoritesMinimum : "-"}</TableCell>
                            <TableCell align="center" padding="checkbox"><IconButton onClick={() => editClick(item)}><EditIcon color="secondary" /></IconButton></TableCell>
                            <TableCell align="center" padding="checkbox">
                                <ConfirmationModal iconButton={true} icon={<DeleteIcon color="secondary" />} message={`${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_1} ${item.name} ${resourceFinal[appLanguage].messages.DELETE_CONFIRMATION_2}`} confirmAction={() => deleteClick(item.id)} />
                            </TableCell>
                            <TableCell align="center" padding="checkbox"><IconButton onClick={() => selectClick(item)}><ArrowForwardIosIcon color="secondary" /></IconButton></TableCell>
                        </TableRow>
                    )}
                    {(rowsPerPage > 0 ? programs.filter(handleFilterResult).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : programs.filter(handleFilterResult)).length === 0 && <TableRow><TableCell colSpan={13}>{resourceFinal[appLanguage].messages.FILTER_RESULT_EMPTY}</TableCell></TableRow>}
                    {emptyRows > 0 && <TableRow style={{ height: 63 * emptyRows }}><TableCell colSpan={13} /></TableRow>}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            sx={{ border: 0 }}
                            rowsPerPageOptions={[10, 25, 50,
                                {
                                    label: programs?.length < 100 ? resourceFinal[appLanguage].words.ALL : '100',
                                    value: programs?.length < 100 ? -1 : 100
                                }
                            ]}
                            count={programs?.filter(handleFilterResult)?.length}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            labelDisplayedRows={({ from, to, count }) => { return `${from}-${to} ${resourceFinal[appLanguage].messages.OF_THE} ${count}`; }}
                            labelRowsPerPage={resourceFinal[appLanguage].messages.TABLE_ROWS_PER_PAGE}
                        />
                    </TableRow>
                </TableFooter>
            </>
        );
    };

    return (
        <>
            <ContentLoader
                successCondition={programsStatus === "success"}
                errorCondition={programsStatus === "failed"}
                content={
                    <Stack sx={{ display: 'flex', flex: '1 1 auto', bgcolor: TableBgcolor, borderRadius: MainBorderRadius, p: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 1 }}>
                            <SearchField initialValue={filter} onDebouncedChange={setFilter} />
                            <Button variant="outlined" onClick={newClick} startIcon={<AddIcon />}>{resourceFinal[appLanguage].words.ADD}</Button>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.NAME}</TableCell>
                                        <TableCell align="left" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].words.DESCRIPTION}</TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader, display: 'none' }}>{resourceFinal[appLanguage].words.NECESSARY} {resourceFinal[appLanguage].words.FLAGS_GREEN_MULT.toLowerCase()}</TableCell>
                                        {siomSettings?.enableAbsenceBlock && <TableCell align="center" sx={{ ...TableHeader }}>{resourceFinal[appLanguage].messages.MAX_ABSENCE_FOR_BLOCK}</TableCell>}
                                        <TableCell align="center">
                                            <Stack>
                                                <Typography sx={{ ...TableHeader, fontSize: 14 }}>{resourceFinal[appLanguage].words.FAVORITES}</Typography>
                                                <Typography sx={{ ...TableHeader, fontSize: 14 }}>{resourceFinal[appLanguage].words.REQUIRED.toLowerCase()}</Typography>
                                            </Stack>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Typography sx={{ ...TableHeader, fontSize: 14 }}>{resourceFinal[appLanguage].words.FAVORITES}</Typography>
                                            <Typography sx={{ ...TableHeader, fontSize: 14 }}>{resourceFinal[appLanguage].words.MINIMUM.toLowerCase()}</Typography>
                                        </TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                        <TableCell align="center" sx={{ ...TableHeader }}></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableFragment />
                            </Table>
                        </TableContainer>
                    </Stack>
                }
            />

            <Modal open={openEdit} onClose={callbackModalEdit}>
                <>
                    <ProgramEditCard handleClose={callbackModalEdit} />
                </>
            </Modal>

            <Modal open={openNew} onClose={callbackModalNew}>
                <>
                    <ProgramEditCard handleClose={callbackModalNew} addNew />
                </>
            </Modal>
        </>
    );
}
